import { Base64 } from 'js-base64';
import config from '@dkrm/config';
import { KNOWN_IMAGE_HOSTS } from '@dkrm/general-libs/constants';
import { removeHttpProtocol } from '@dkrm/general-libs/Utils/urlUtils';
import { DEFAULT_DPR, DEFAULT_SRCSET_WIDTH_TOLERANCE, DPR_QUALITIES, MAX_SRCSET_WIDTH, MIN_SRCSET_WIDTH } from './constants';
const targetWidthsCache = {};
export const shallowCompareProps = (obj1, obj2) => Object.keys(obj1).length === Object.keys(obj2).length && Object.keys(obj1).every(key => obj1[key] === obj2[key]);
export const buildSrcSet = (path, params = {}, options = {}) => {
  const {
    width,
    height
  } = params;
  if (width || height) {
    return _buildDPRSrcSet(path, params, options);
  }
  return _buildSrcSetPairs(path, params, options);
};
const buildURL = (rawPath = '', params = {}, options = {}) => {
  const path = sanitizePath(rawPath, options);
  const finalParams = _buildParams(params, options);
  // if (!!this.settings.secureURLToken) {
  //   finalParams = this._signParams(path, finalParams);
  // }
  return config.API_URL_MEDIA_CDN + path + finalParams;
};
const genTargetWidths = (minWidth = 100, maxWidth = 8192, widthTolerance = 0.08, cache = {}) => {
  const minW = Math.floor(minWidth);
  const maxW = Math.floor(maxWidth);
  validateRange(minWidth, maxWidth);
  validateWidthTolerance(widthTolerance);
  const cacheKey = `${widthTolerance}/${minW}/${maxW}`;

  // First, check the cache.
  if (cacheKey in cache) {
    return cache[cacheKey];
  }
  if (minW === maxW) {
    return [minW];
  }
  const resolutions = [];
  let currentWidth = minW;
  while (currentWidth < maxW) {
    // While the currentWidth is less than the maxW, push the rounded
    // width onto the list of resolutions.
    resolutions.push(Math.round(currentWidth));
    currentWidth *= 1 + widthTolerance * 2;
  }

  // At this point, the last width in resolutions is less than the
  // currentWidth that caused the loop to terminate. This terminating
  // currentWidth is greater than or equal to the maxW. We want to
  // to stop at maxW, so we make sure our maxW is larger than the last
  // width in resolutions before pushing it (if it's equal we're done).
  if (resolutions[resolutions.length - 1] < maxW) {
    resolutions.push(maxW);
  }

  // eslint-disable-next-line no-param-reassign
  cache[cacheKey] = resolutions;
  return resolutions;
};
const validateDevicePixelRatios = devicePixelRatios => {
  if (!Array.isArray(devicePixelRatios) || !devicePixelRatios.length) {
    throw new Error('The devicePixelRatios argument can only be passed a valid non-empty array of integers');
  } else {
    const allValidDPR = devicePixelRatios.every(dpr => {
      return dpr >= 1 && dpr <= 5;
    });
    if (!allValidDPR) {
      throw new Error('The devicePixelRatios argument can only contain positive integer values between 1 and 5');
    }
  }
};
export function validateAndDestructureOptions(options) {
  let widthTolerance;
  if (options.widthTolerance !== undefined) {
    validateWidthTolerance(options.widthTolerance);
    // eslint-disable-next-line prefer-destructuring
    widthTolerance = options.widthTolerance;
  } else {
    widthTolerance = DEFAULT_SRCSET_WIDTH_TOLERANCE;
  }
  const minWidth = options.minWidth === undefined ? MIN_SRCSET_WIDTH : options.minWidth;
  const maxWidth = options.maxWidth === undefined ? MAX_SRCSET_WIDTH : options.maxWidth;

  // Validate the range unless we're using defaults for both
  if (minWidth !== MIN_SRCSET_WIDTH || maxWidth !== MAX_SRCSET_WIDTH) {
    validateRange(minWidth, maxWidth);
  }
  return [widthTolerance, minWidth, maxWidth];
}
const validateWidthTolerance = widthTolerance => {
  if (widthTolerance < 0.01) {
    throw new Error('The srcset widthTolerance must be a number greater than or equal to 0.01');
  }
};
const validateRange = (min, max) => {
  if (!(Number.isInteger(min) && Number.isInteger(max)) || min <= 0 || max <= 0 || min > max) {
    throw new Error(`The min and max srcset widths can only be passed positive Number values, and min must be less than max. Found min: ${min} and max: ${max}.`);
  }
};
const validateWidths = customWidths => {
  if (!customWidths.length) {
    throw new Error('The widths argument can only be passed a valid non-empty array of integers');
  } else {
    const allPositiveIntegers = customWidths.every(width => {
      return Number.isInteger(width) && width > 0;
    });
    if (!allPositiveIntegers) {
      throw new Error('A custom widths argument can only contain positive integer values');
    }
  }
};
const _buildDPRSrcSet = (path, params = {}, options = {}) => {
  if (options.devicePixelRatios) {
    validateDevicePixelRatios(options.devicePixelRatios);
  }
  const targetRatios = options.devicePixelRatios || DEFAULT_DPR;
  const disableVariableQuality = options.disableVariableQuality || false;
  const qualities = {
    ...DPR_QUALITIES,
    ...(options.variableQualities || {})
  };
  const withQuality = dpr => {
    return `${buildURL(path, {
      ...params,
      dpr,
      quality: params.quality || qualities[dpr] || qualities[Math.floor(dpr)]
    }, options)} ${dpr}x`;
  };
  const srcset = disableVariableQuality ? targetRatios.map(dpr => `${buildURL(path, {
    ...params,
    dpr
  }, options)} ${dpr}x`) : targetRatios.map(dpr => withQuality(dpr));
  return srcset.join(',\n');
};
const _buildSrcSetPairs = (path, params = {}, options = {}) => {
  const [widthTolerance, minWidth, maxWidth] = validateAndDestructureOptions(options);
  let targetWidthValues;
  if (options.widths) {
    validateWidths(options.widths);
    targetWidthValues = [...options.widths];
  } else {
    targetWidthValues = genTargetWidths(minWidth, maxWidth, widthTolerance, targetWidthsCache);
  }
  const srcset = targetWidthValues.map(w => `${buildURL(path, {
    ...params,
    width: w
  }, options)} ${w}w`);
  return srcset.join(',\n');
};

/**
 * "Sanitize" the path of the image URL.
 * Ensures that the path has a leading slash, and that the path is correctly
 * encoded. If it's a proxy path (begins with http/https), then encode the
 * whole path as a URI component, otherwise only encode specific characters.
 * @param {string} path The URL path of the image
 * @param {Object} options Sanitization options
 * @param {boolean} options.encode Whether to encode the path, default true
 * @returns {string} The sanitized path
 */
export const sanitizePath = (path, options = {}) => {
  // Strip leading slash first (we'll re-add after encoding)
  let _path = path.replace(/^\//, '');
  if (options.disablePathEncoding) {
    return `/${_path}`;
  }
  if (options.encoder) {
    _path = options.encoder(_path);
  } else if (/^https?:\/\//.test(_path)) {
    // Use de/encodeURIComponent to ensure *all* characters are handled,
    // since it's being used as a path
    _path = encodeURIComponent(_path);
  } else {
    // Use de/encodeURI if we think the path is just a path,
    // so it leaves legal characters like '/' and '@' alone
    _path = encodeURI(_path).replace(/[#?:+]/g, encodeURIComponent);
  }
  return `/${_path}`;
};
const _buildParams = (params = {}, options = {}) => {
  // If a custom encoder is present, use it
  // Otherwise just use the encodeURIComponent
  const customEncoder = options.encoder;
  const queryParams = [
  // Map over the key-value pairs in params while applying applicable encoding.
  ...Object.entries(params).reduce((prev, [key, value]) => {
    if (value == null) {
      return prev;
    }
    const encodedKey = customEncoder ? customEncoder(key, value) : encodeURIComponent(key);
    const encodedValue =
    // eslint-disable-next-line no-nested-ternary
    key.substring(-2) === '64' ? customEncoder ? customEncoder(value, key) : Base64.encodeURI(value) : customEncoder ? customEncoder(value, key) : encodeURIComponent(value);
    prev.push(`${encodedKey}=${encodedValue}`);
    return prev;
  }, [])];
  return `${queryParams.length > 0 ? '?' : ''}${queryParams.join('&')}`;
};
export const isImageUriAbleToUseSmartImage = uri => KNOWN_IMAGE_HOSTS.some(host => uri.includes(removeHttpProtocol(host)));